/**
 * App-level clipboard model for copy/pasting calendar time entries.
 *
 * The calendar keeps a single copied entry in a ref and, on paste,
 * recreates it at the slot under the cursor with the same duration.
 */

export interface ClipboardEntry {
  projectId: string;
  designationId: string;
  description: string;
  tags: string[];
  billable: boolean;
  /** Original entry length in minutes — preserved on paste. */
  durationMinutes: number;
}

export interface PastePayload {
  projectId: string;
  designationId: string;
  description: string;
  tags: string[];
  billable: boolean;
  date: Date;
  startMinute: number;
  endMinute: number;
}

/**
 * Build the time-entry payload for pasting a copied entry at a target
 * slot. The end is clamped to the end of the day (1440 minutes) so a
 * late-evening paste never spills past midnight.
 */
export function buildPastePayload(
  clip: ClipboardEntry,
  target: { date: Date; startMinute: number },
): PastePayload {
  const endMinute = Math.min(1440, target.startMinute + clip.durationMinutes);
  return {
    projectId: clip.projectId,
    designationId: clip.designationId,
    description: clip.description,
    tags: clip.tags,
    billable: clip.billable,
    date: target.date,
    startMinute: target.startMinute,
    endMinute,
  };
}
